import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, RefreshCw, Users, ShoppingBag } from 'lucide-react';
import { Coupon, CouponUsageLog } from '@/types/coupon';
import { couponApi } from '@/services/couponApi';
import toast from 'react-hot-toast';

export const CouponForm: React.FC = () => {
  const navigate = useNavigate();
  const { couponId } = useParams();
  const isEditMode = !!couponId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usageLogs, setUsageLogs] = useState<CouponUsageLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    type: 'percentage' as 'percentage' | 'fixed',
    discountValue: 0,
    minOrderAmount: 0,
    maxUsesTotal: 0,
    maxUsesPerUser: 1,
    usedCount: 0,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    scope: 'all' as 'all' | 'categories' | 'products' | 'customers',
    isActive: true,
    applicableCategories: [] as string[],
    applicableProducts: [] as string[],
    applicableCustomers: [] as string[],
  });

  useEffect(() => {
    if (isEditMode) {
      loadCoupon();
      loadUsageLogs();
    }
  }, [couponId]);

  const loadUsageLogs = async () => {
    if (!couponId) return;
    try {
      setLogsLoading(true);
      const logs = await couponApi.getUsageLogs(couponId);
      setUsageLogs(logs);
    } catch (error: any) {
      console.error('Usage logs yüklenirken hata:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const loadCoupon = async () => {
    try {
      setLoading(true);
      if (!couponId) return;
      const data = await couponApi.getCoupon(couponId);
      setFormData({
        code: data.code,
        description: data.description || '',
        type: data.type,
        discountValue: data.discountValue,
        minOrderAmount: data.minOrderAmount,
        maxUsesTotal: data.maxUsesTotal || 0,
        maxUsesPerUser: data.maxUsesPerUser,
        usedCount: data.usedCount,
        startDate: new Date(data.startDate).toISOString().split('T')[0],
        endDate: new Date(data.endDate).toISOString().split('T')[0],
        scope: data.scope,
        isActive: data.isActive,
        applicableCategories: data.applicableCategories || [],
        applicableProducts: data.applicableProducts || [],
        applicableCustomers: data.applicableCustomers || [],
      });
    } catch (error: any) {
      console.error('Kupon yüklenirken hata:', error);
      toast.error(error.message || 'Kupon yüklenemedi');
      navigate('/admin/coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const finalValue = (type === 'checkbox') ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({
      ...prev,
      [name]: finalValue,
    }));
  };

  const generateCouponCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({
      ...prev,
      code: code,
    }));
    toast.success(`✅ Kupon kodu oluşturuldu: ${code}`);
  };

  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validasyon
    const errors: string[] = [];
    if (!formData.code.trim()) errors.push('Kupon kodu zorunludur');
    if (formData.discountValue <= 0) errors.push('İndirim değeri 0 dan büyük olmalıdır');
    if (formData.maxUsesPerUser <= 0) errors.push('Maksimum kullanım sayısı 0 dan büyük olmalıdır');
    if (!formData.startDate) errors.push('Başlama tarihi zorunludur');
    if (!formData.endDate) errors.push('Bitiş tarihi zorunludur');
    if (new Date(formData.startDate) >= new Date(formData.endDate)) errors.push('Bitiş tarihi başlama tarihinden sonra olmalıdır');

    if (errors.length > 0) {
      toast.error(errors.join('\n'));
      return;
    }

    try {
      setSaving(true);

      const dataToSubmit = {
        ...formData,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
      };

      if (isEditMode && couponId) {
        await couponApi.updateCoupon(couponId, dataToSubmit);
        toast.success('✅ Kupon güncellendi!');
      } else {
        await couponApi.createCoupon(dataToSubmit);
        toast.success('✅ Kupon oluşturuldu!');
      }
      navigate('/admin/coupons');
    } catch (error: any) {
      toast.error(error.message || 'İşlem başarısız oldu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/coupons')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition"
        >
          <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {isEditMode ? '✏️ Kuponu Düzenle' : '📝 Yeni Kupon Oluştur'}
          </h1>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 space-y-6">
        {/* Kupon Kodu */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Kupon Kodu *
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              placeholder="SUMMER20"
              className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isEditMode}
            />
            {!isEditMode && (
              <button
                type="button"
                onClick={generateCouponCode}
                className="px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition flex items-center gap-2 whitespace-nowrap"
              >
                <RefreshCw size={16} />
                Otomatik Oluştur
              </button>
            )}
          </div>
        </div>

        {/* Açıklama */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Açıklama
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Kupon açıklaması..."
            rows={3}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* İndirim Türü ve Değeri */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              İndirim Türü *
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="percentage">Yüzde (%)</option>
              <option value="fixed">Sabit Tutar (₺)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              İndirim Değeri *
            </label>
            <input
              type="number"
              name="discountValue"
              value={formData.discountValue}
              onChange={handleNumericChange}
              min="0"
              step="1"
              placeholder={formData.type === 'percentage' ? '20' : '100'}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Minimum Sipariş Tutarı */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Min. Sipariş Tutarı (₺) *
          </label>
          <input
            type="number"
            name="minOrderAmount"
            value={formData.minOrderAmount}
            onChange={handleNumericChange}
            min="0"
            step="1"
            placeholder="0"
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Kullanım Limitleri */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Toplam Kullanım Limiti
            </label>
            <input
              type="number"
              name="maxUsesTotal"
              value={formData.maxUsesTotal}
              onChange={handleNumericChange}
              min="0"
              step="1"
              placeholder="0"
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Tüm kullanıcılar toplamı. 0 = Sınırsız</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Kullanıcı Başı Limit *
            </label>
            <input
              type="number"
              name="maxUsesPerUser"
              value={formData.maxUsesPerUser}
              onChange={handleNumericChange}
              min="1"
              step="1"
              placeholder="1"
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Bir kullanıcı en fazla kaç kez kullanabilir</p>
          </div>
        </div>

        {/* Başlama ve Bitiş Tarihleri */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Başlama Tarihi *
            </label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Bitiş Tarihi *
            </label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Aktif */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isActive"
            name="isActive"
            checked={formData.isActive}
            onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="isActive" className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            Kupon Aktif
          </label>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={() => navigate('/admin/coupons')}
            className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition"
          >
            <Save size={18} />
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </form>

      {/* Usage Logs Section - Only in edit mode */}
      {isEditMode && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 mt-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Users size={20} />
            Kullanım Geçmişi
            {usageLogs.length > 0 && (
              <span className="text-sm font-normal text-gray-500">({usageLogs.length} kayıt)</span>
            )}
          </h2>

          {logsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin">
                <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
              </div>
            </div>
          ) : usageLogs.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-6">Henüz kullanım kaydı yok</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Kullanıcı</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Sipariş</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">İndirim</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Tarih</th>
                  </tr>
                </thead>
                <tbody>
                  {usageLogs.map((log) => (
                    <tr key={log.id} className="border-b border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3 text-sm">
                        <div className="text-gray-900 dark:text-gray-100 font-medium">{log.userName || '-'}</div>
                        <div className="text-gray-500 dark:text-gray-400 text-xs">{log.userEmail || log.userId || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {log.orderNo ? (
                          <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 font-medium">
                            <ShoppingBag size={14} />
                            #{log.orderNo}
                          </span>
                        ) : log.orderId ? (
                          <span className="text-gray-600 dark:text-gray-400 text-xs font-mono">{log.orderId.substring(0, 8)}...</span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {log.discountAmount ? `₺${log.discountAmount.toFixed(2)}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {log.usedAt ? new Date(log.usedAt).toLocaleString('tr-TR') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CouponForm;
